import clsx from 'clsx';
import { addDays, format, isAfter, parse } from 'date-fns';
import moment from 'moment';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useAuth } from '@/api';
import { useLiveGoldPrice } from '@/api/avail-loan/useLiveGoldPrice';
import type { CustomerActiveAppointment, CustomerLoan } from '@/api/dashboard';
import {
  useCustomerActiveAppointment,
  useTopupEligibility,
} from '@/api/dashboard';
import { useAllCustomerLoans } from '@/api/dashboard/useAllCustomerLoans';
import { useFeatureFlags } from '@/api/feature-flags';
import atHome from '@/assets/images/icons/athome.svg';
import seamlessHome from '@/assets/images/icons/quickapproval.svg';
import type { LoanInputOption } from '@/common';
import { useNotificationStore, useUserStore } from '@/common';
import useMoengageStore from '@/store/moengage';
import { StringPatterns } from '@/utils';
import { GoldLoanSEO } from '@/utils/constants/MetaData';
import { calculateLTV, formatDate, formatTime } from '@/utils/functions';
import { createMoengageEvent } from '@/utils/moengage';

import CloseIcon from '../../../assets/images/icons/close-icon.svg';
import GoldCoin from '../../../assets/images/icons/gold-coin-icon.svg';
import GoldRateIcon from '../../../assets/images/icons/gold-rate.svg';
import HelpIcon from '../../../assets/images/icons/help-icon.svg';
import InterestRateIcon from '../../../assets/images/icons/interest-rate.svg';
import RupeeIcon from '../../../assets/images/icons/rupee-icon.svg';
import WarningIcon from '../../../assets/images/icons/warning-icon.svg';
import AttractiveSchemes from '../gold-loan/AttractiveSchemes';
import GlfhFaq from '../gold-loan/GlfhFaq';
import GlNewNote from '../gold-loan/GlNewNote';
import Loader from '../Loader';

const DisplayAppointmentDetails = ({
  appointmentDetails,
  goldPrice,
}: {
  appointmentDetails: CustomerActiveAppointment | undefined;
  goldPrice: number;
}) => {
  const router = useRouter();

  const goToPledge = () => {
    router.push('/gold-loans/loan-appointment');
  };

  const goToReleaseAppointment = () => {
    router.push(
      `/gold-loans/release-appointment?id=${appointmentDetails?.id || ''}`
    );
  };

  const [minGoldRequired, setMinGoldRequired] = useState<number>(0);

  useEffect(() => {
    setMinGoldRequired(
      calculateLTV(
        appointmentDetails?.loanAmountRequested || '',
        goldPrice,
        'AMOUNT-TO-GOLD'
      )
    );
  }, [appointmentDetails, goldPrice]);

  const { featureFlags } = useFeatureFlags();

  return (
    <>
      <Head>
        <title>{GoldLoanSEO.metaTitle}</title>
        <meta name="description" content={GoldLoanSEO.metaDescription} />
        <meta
          property="og:description"
          content={GoldLoanSEO.metaOGDescription}
        />
      </Head>
      {featureFlags.release_gl_release &&
      appointmentDetails?.loanStatus === 'LOAN_RELEASE' ? (
        <div
          className="insideCard mfone-cursor-pointer"
          onClick={goToReleaseAppointment}
        >
          <div className="d-flex align-items-center justify-content-between">
            <p className="loanCard__title text--bold text--black">
              Gold Release
            </p>
            <a className="circleArrow-icon"></a>
          </div>
          <div className="requiredGold">
            <p className="text--black text--semi title">
              <Image
                className="img-fluid"
                src={GoldCoin}
                alt="gold coin icon"
              />{' '}
              Loan Amount Paid
            </p>
            <p className="pincode__edit text--semi text--gray">
              ₹{appointmentDetails?.pledgeAmount || 0}
            </p>
          </div>
          <div className="requiredGold">
            <p className="text--black text--semi title">
              <Image
                className="img-fluid"
                src={GoldCoin}
                alt="gold coin icon"
              />{' '}
              Gold to Release
            </p>
            <p className="pincode__edit text--semi text--gray">
              {appointmentDetails.pledgeGoldWeight || 0} gms
            </p>
          </div>
          <div className="appointmentCard mfone_releaseCard">
            <div className="d-flex align-items-center justify-content-between">
              <p className="title text--bold text--green">
                Gold Release Appointment Scheduled
              </p>
            </div>
            <p className="text--regular text--gray desc">
              Your appointment request has been generated. We will share the
              details of the loan managers before the appointment date.
            </p>
            <p className="text--regular time">
              Visit slot:{' '}
              <span className="text--bold">
                {formatTime(appointmentDetails?.appointmentStartTime || '')},{' '}
                {formatDate(appointmentDetails?.currentAppointmentDate || '')}
              </span>
            </p>
          </div>
        </div>
      ) : (
        <div className="insideCard mfone-cursor-pointer" onClick={goToPledge}>
          <div className="d-flex align-items-center justify-content-between">
            <p className="loanCard__title text--bold text--black">
              Loan Requirement
            </p>
            <a className="circleArrow-icon"></a>
          </div>
          <div className="requiredGold">
            <p className="text--black text--semi title">
              <Image
                className="img-fluid"
                src={GoldCoin}
                alt="gold coin icon"
              />{' '}
              Min gold required for the loan
            </p>
            <p className="pincode__edit text--semi text--gray">
              {minGoldRequired} gms
            </p>
          </div>
          <div className="appointmentCard">
            <div className="d-flex align-items-center justify-content-between">
              <p className="title text--bold text--green">
                Appointment Scheduled
              </p>
              {/* <a href="#" className="circleArrow-icon green"></a> */}
            </div>
            <p className="text--regular text--gray desc">
              Your appointment request has been generated. We will share the
              details of the loan managers before the appointment date
            </p>
            <p className="text--regular time">
              Visit slot:{' '}
              <span className="text--bold">
                {formatTime(appointmentDetails?.appointmentStartTime || '')},{' '}
                {formatDate(appointmentDetails?.currentAppointmentDate || '')}
              </span>
            </p>
          </div>
          <GlNewNote />
        </div>
      )}
    </>
  );
};

const getNextInterestDate = (dateString: string) => {
  let formattedDate = '';
  try {
    formattedDate = format(
      addDays(parse(dateString, 'yyyy-MM-dd', new Date()), 30),
      'yyyy-MM-dd'
    );
  } catch {
    formattedDate = '';
  }

  return formattedDate;
};

const DisplayLoanDetails = ({
  customerLoans,
  goldPrice,
}: {
  customerLoans: CustomerLoan[];
  goldPrice: number;
}) => {
  const router = useRouter();
  const goToLoanProfile = (loanId: string) => {
    router.push(`/gold-loans/loan-profile?id=${loanId}`);
  };
  // const goToRepayment = (
  //   loanId: string,
  //   e: React.MouseEvent<HTMLAnchorElement>
  // ) => {
  //   e.stopPropagation();
  //   e.preventDefault();
  //   router.push(`/gold-loans/repayment?id=${loanId}`);
  // };

  const { setUCIC } = useUserStore((state) => ({
    setUCIC: state.setUCIC,
  }));

  useEffect(() => {
    const customerUcic = customerLoans[0]?.ucic || '';

    setUCIC(customerUcic);
  }, [customerLoans]);

  const getLoanDueAmount = (loan: CustomerLoan) => {
    return loan.interestAmount + loan.postageAmount - loan.partAmount;
  };

  // const { mobileNumber: mobNumber, glRepayNowClicked } = useMoengageStore(
  //   (state) => {
  //     return {
  //       mobileNumber: state.mobileNumber,
  //       glRepayNowClicked: state.glRepayNowClicked,
  //     };
  //   }
  // );
  const { featureFlags } = useFeatureFlags();

  // const { data: userData } = useAuth();

  const hasDueDatePassed = (loan: CustomerLoan) => {
    const today = format(new Date(), 'yyyy-MM-dd');
    const { loanDueDate } = loan;

    if (!loanDueDate) {
      return false;
    }

    return isAfter(new Date(today), new Date(loanDueDate));
  };

  return (
    <ul>
      {customerLoans.map((loan) => {
        if (
          featureFlags.release_gl_release &&
          loan.totalAmountToPay === 0 &&
          loan.loanGivenBy === 'CGL_GLFH'
        ) {
          return (
            <div
              className="insideCard mfone-cursor-pointer"
              key={loan.id}
              onClick={() => {
                goToLoanProfile(loan.id);
              }}
            >
              <div className="d-flex  justify-content-between">
                <p className="loanCard__title text--bold text--black pb-2">
                  <div className="pb-2">Schedule an Appointment</div>
                  <span className="chips chips--lightBlue">
                    Loan No:{' '}
                    {loan.loanNumber ? (
                      loan.loanNumber
                    ) : (
                      <span className="text--gray text--regular">N/A</span>
                    )}
                  </span>
                </p>
                {loan.loanGivenBy === 'CGL_GLFH' && (
                  <p className="circleArrow-icon"></p>
                )}
              </div>
              <div className="requiredGold">
                <p className="text--black text--semi title">
                  <Image
                    className="img-fluid"
                    src={GoldCoin}
                    alt="gold coin icon"
                  />{' '}
                  Loan Amount Paid
                </p>
                <p className="pincode__edit text--semi text--gray">
                  ₹{loan.pledgeAmount}
                </p>
              </div>
              <div className="requiredGold">
                <p className="text--black text--semi title">
                  <Image
                    className="img-fluid"
                    src={GoldCoin}
                    alt="gold coin icon"
                  />{' '}
                  Gold to Release
                </p>
                <p className="pincode__edit text--semi text--gray">
                  {calculateLTV(loan.pledgeAmount, goldPrice, 'AMOUNT-TO-GOLD')}{' '}
                  gms
                </p>
              </div>
              <div className="appointmentCard mfone_releaseCard">
                <div className="d-flex align-items-center justify-content-between">
                  <p className="title text--bold text--blue">Loan Closed</p>
                </div>
                <p className="text--regular text--gray desc">
                  Please release your gold either by booking a home visit
                  appointment or visit your branch
                </p>
                <Link
                  href=""
                  onClick={(e) => {
                    e.stopPropagation();
                    router.push(`/gold-loans/release-loan?id=${loan.id}`);
                  }}
                  className={clsx(
                    'custom__button custom__button--dark text--semi mfone-cursor-pointer'
                  )}
                  id="GL_loan-release"
                >
                  Release Your Gold
                </Link>
              </div>
            </div>
          );
        }

        return (
          <div
            className={clsx(
              'insideCard position-relative overflow-hidden',
              loan.loanGivenBy === 'CGL_GLFH' && 'mfone-cursor-pointer'
            )}
            key={loan.id}
            onClick={() => {
              if (loan.loanGivenBy === 'CGL_GLFH') {
                goToLoanProfile(loan.id);
              }
            }}
          >
            <div className="d-flex align-items-center justify-content-between">
              <div className="chips__container">
                {/* <span className="chips chips--lightBlue">Original Loans</span> */}
                <span className="chips chips--lightBlue">
                  Loan No:{' '}
                  {loan.loanNumber ? (
                    loan.loanNumber
                  ) : (
                    <span className="text--gray text--regular">N/A</span>
                  )}
                </span>
                <span className="chips chips--lightBlue">
                  UCIC{' '}
                  {loan.ucic ? (
                    loan.ucic
                  ) : (
                    <span className="text--gray text--regular">N/A</span>
                  )}
                </span>
              </div>
              {loan.loanGivenBy === 'CGL_GLFH' && (
                <p className="circleArrow-icon"></p>
              )}
            </div>
            <div className="loanCard__dates">
              <div className="left">
                <span className="text--regular text--gray title">
                  Due Today
                </span>
                <span className="text--bold text--darkBlue price">
                  {typeof loan.interestAmount === 'number' ? (
                    `₹ ${getLoanDueAmount(loan)}`
                  ) : (
                    <span className="text--gray text--regular">N/A</span>
                  )}
                </span>
              </div>
              <div className="right">
                <span className="text--regular text--gray title">
                  Next Due Date
                </span>
                <span className="text--bold text--darkBlue price">
                  {loan.lastIntPaidDate ? (
                    getNextInterestDate(loan.lastIntPaidDate)
                  ) : (
                    <span className="text--gray text--regular">N/A</span>
                  )}
                </span>
              </div>
            </div>
            <div className="loanCard__amount">
              <div className="item">
                <span className="text--gray text--regular title">Loan Amt</span>
                <span className="text--black text--bold price">
                  {typeof loan.pledgeAmount === 'number' ? (
                    `₹ ${loan.pledgeAmount}`
                  ) : (
                    <span className="text--gray text--regular">N/A</span>
                  )}
                </span>
              </div>
              <div className="item text-center">
                <span className="text--gray text--regular title">
                  Total Amt Due
                </span>
                <span className="text--black text--bold price">
                  {typeof loan.totalAmountToPay === 'number' ? (
                    `₹ ${loan.totalAmountToPay}`
                  ) : (
                    <span className="text--gray text--regular">N/A</span>
                  )}
                </span>
              </div>
              <div className="item text-right">
                <span className="text--gray text--regular title">
                  Interest Rate
                </span>
                <span className="text--black text--bold price">
                  {typeof loan.currentRateOfInterest === 'number' ? (
                    `${(loan.currentRateOfInterest / 12).toFixed(2)}% p.m`
                  ) : (
                    <span className="text--gray text--regular">-</span>
                  )}
                </span>
              </div>
            </div>
            {loan.paymentAllowable === true ? (
              <></>
            ) : (
              <p className="text--gray text--regular pledge__info yellow mb-2 mt-4">
                <span className="text--yellow text--bold">
                  We encountered a slight hiccup:{' '}
                </span>
                Please wait for about 30 minutes. If the issue persists, please
                visit your Muthoot FinCorp branch.
              </p>
            )}
            {loan.totalAmountToPay > 0 && (
              <Link
                href=""
                // DISABLING REPAY JOURNEY
                // onClick={(e) => {
                //   createMoengageEvent(glRepayNowClicked, {
                //     created_date: moment().format("YYYY-MM-DD HH:mm:ss"),
                //     mobile_number: mobNumber,
                //     live_gold_price: goldPrice,
                //     active_loan_amount: loan.loanAmountRequested,
                //     email: userData?.emailAddress,
                //     profile_id: userData?.id,
                //     monthly_due: "",
                //     total_amount_due: loan.totalAmountToPay,
                //     interest_rate: loan.rateOfInterest,
                //     overdue: "",
                //     loan_id: loan.id,
                //   });
                //   if (loan.paymentAllowable) {
                //     goToRepayment(loan.id, e);
                //   }
                // }}
                className={clsx(
                  'custom__button custom__button--dark text--semi disabled'
                  // loan.paymentAllowable === true ? '' : 'disabled'
                )}
                id="GL_loan-details_repay"
              >
                Repay Now
              </Link>
            )}
            {hasDueDatePassed(loan) &&
              loan.totalAmountToPay > 0 &&
              loan.loanGivenBy !== 'CGL_GLFH' &&
              loan.loanGivenBy !== 'CGL_NON_GLFH' && (
                <div className="mfone_blocked_repayment text--darkBlue d-flex justify-content-center align-items-center text--bold p-3 text-center">
                  <div>
                    <div className="mb-3">Your Loan Tenure is Over</div>
                    <p>
                      For any further queries or pending repayments, please head
                      to your branch
                    </p>
                  </div>
                </div>
              )}
          </div>
        );
      })}
    </ul>
  );
};

type FormValues = {
  loanAmountInput: string;
  goldWeightInput: string;
};

export const Dashboard = () => {
  const [display, setDisplay] = useState({
    displayLoansDetails: true,
    displayAppointmentDetails: true,
  });
  const [customerLoans, setCustomerLoans] = useState<CustomerLoan[]>([]);
  const { userDetails } = useAuth();
  const { saveAppointmentId, saveInputs, saveTopupData } = useUserStore(
    (state) => ({
      saveAppointmentId: state.setAppointmentId,
      saveInputs: state.setInputs,
      saveTopupData: state.setTopupData,
    })
  );

  const [customerId, setCustomerId] = useState<string>('');
  const { addNotification } = useNotificationStore();

  useEffect(() => {
    if (userDetails?.id) {
      setCustomerId(userDetails.id);
    } else {
      setCustomerId('');
    }
  }, [userDetails]);

  const { fetchCustomerLoans } = useAllCustomerLoans();
  const { activeAppointment } = useCustomerActiveAppointment(customerId);
  const [loanInputOption, setLoanInputOption] =
    useState<LoanInputOption>('amount');
  const { liveGoldPrice: liveGoldPricePerGram } = useLiveGoldPrice();

  useEffect(() => {
    saveAppointmentId('');
  }, []);

  useEffect(() => {
    if (!activeAppointment?.id) {
      saveAppointmentId('');
      return;
    }

    saveAppointmentId(activeAppointment.id);
  }, [activeAppointment]);

  const { mobileNumber: mobNumber, glApplyNowClicked } = useMoengageStore(
    (state) => {
      return {
        mobileNumber: state.mobileNumber,
        glApplyNowClicked: state.glApplyNowClicked,
      };
    }
  );

  useEffect(() => {
    if (customerId) {
      fetchCustomerLoans(customerId).then((loans) => {
        setCustomerLoans(
          loans.filter(
            (loan) =>
              loan.loanStatus === 'DISBURSED' ||
              loan.loanStatus === 'LOAN_RELEASE'
          )
        );
      });
    }
  }, [customerId]);

  const handleClick = (button: 'all' | 'loans' | 'appointment') => {
    if (button === 'all') {
      setDisplay({
        displayLoansDetails: true,
        displayAppointmentDetails: true,
      });
    }
    if (button === 'loans') {
      setDisplay({
        displayLoansDetails: true,
        displayAppointmentDetails: false,
      });
    }
    if (button === 'appointment') {
      setDisplay({
        displayLoansDetails: false,
        displayAppointmentDetails: true,
      });
    }
  };

  const {
    register,
    formState: { errors },
    watch,
    handleSubmit,
  } = useForm<FormValues>();

  const router = useRouter();
  const onFormSubmit = (data: FormValues) => {
    createMoengageEvent(glApplyNowClicked, {
      created_date: moment().format('YYYY-MM-DD HH:mm:ss'),
      mobile_number: mobNumber,
      is_login: true,
      in_grams: loanInputOption == 'weight',
      in_rupee: loanInputOption == 'amount',
      selected_amount: data.loanAmountInput,
      selected_weight: data.goldWeightInput,
      live_gold_price: liveGoldPricePerGram,
      live_buying_price: liveGoldPricePerGram,
      service_type: 'GL',
    });
    saveInputs({
      selectedOption: loanInputOption,
      requestedValue:
        loanInputOption === 'amount'
          ? data.loanAmountInput
          : data.goldWeightInput,
    }).then(() => {
      if (activeAppointment?.id) {
        addNotification({
          title: 'Cannot book appointment',
          type: 'warning',
          message: 'You already have an appointment',
        });
        return;
      }

      router.push('/gold-loans/new-loan');
    });
  };

  const { featureFlags } = useFeatureFlags();

  const { checkTopupEligibility, isLoading: isCheckingTopupEligibiliy } =
    useTopupEligibility();

  const notEligibleModalRef = useRef<HTMLAnchorElement>(null);

  const openTopupFlow = () => {
    const mobileNumber =
      userDetails?.businessPhone ||
      // userDetails?.mobilePhone ||
      // userDetails?.homePhone ||
      '';

    checkTopupEligibility({ mobileNumber }).then((resData) => {
      if (resData.maxCreditAmount >= 1000) {
        saveTopupData({
          ...resData,
        }).then(() => {
          router.push('/gold-loans/top-up');
        });
      } else {
        notEligibleModalRef.current?.click();
      }
    });
  };

  return (
    <>
      <main className="home afterLogin mfone-gl-dashboard">
        <section className="bg bg--light hero">
          <div className="container">
            <div className="row">
              <div className="col-lg-7 visitSchedule loanDetails">
                <div className="loanDetails__card loanCard yourLoan">
                  <p className="loanCard__title text--bold text--black">
                    Your Loans
                  </p>
                  <div className="radio__chips-container mfone-overflow-auto">
                    <label className="radio__chips" htmlFor="All Loan">
                      <input
                        type="radio"
                        name="yourLoans"
                        id="All Loan"
                        value="All Loan"
                        onClick={() => handleClick('all')}
                        defaultChecked
                      />
                      <p className="mfone-whitespace-nowrap">
                        All{' '}
                        {customerLoans.length +
                          (activeAppointment != null ? 1 : 0)}
                      </p>
                    </label>
                    <label className="radio__chips" htmlFor="activeLoan">
                      <input
                        type="radio"
                        name="yourLoans"
                        id="activeLoan"
                        value="Active Loans"
                        onClick={() => handleClick('loans')}
                      />
                      <p className="mfone-whitespace-nowrap">
                        Active Loans {customerLoans.length}
                      </p>
                    </label>
                    <label className="radio__chips" htmlFor="appointmentLoan">
                      <input
                        type="radio"
                        name="yourLoans"
                        id="appointmentLoan"
                        value="Appointments"
                        onClick={() => handleClick('appointment')}
                      />
                      <p className="mfone-whitespace-nowrap">
                        Appointments {activeAppointment != null ? 1 : 0}{' '}
                      </p>
                    </label>
                  </div>
                  {display.displayLoansDetails ? (
                    <DisplayLoanDetails
                      customerLoans={customerLoans}
                      goldPrice={liveGoldPricePerGram}
                    />
                  ) : // ? DisplayLoanDetails(customerLoans)
                  null}
                  {display.displayAppointmentDetails && activeAppointment ? (
                    <DisplayAppointmentDetails
                      appointmentDetails={activeAppointment}
                      goldPrice={liveGoldPricePerGram}
                    />
                  ) : null}
                </div>

                <div className="loanDetails__card loanCard">
                  <div className="loanRequirements">
                    <p className="loanCard__title text--bold text--black">
                      Gold loan Calculator
                    </p>
                    <div className="livePrice">
                      <p className="liveChips text--semi text--white">
                        <span className="pulsingDot"></span> Live
                      </p>
                      <p className="text--regular text--black title">
                        Loan to value:{' '}
                        <span className="text--bold">
                          ₹{liveGoldPricePerGram}/gm**
                        </span>
                      </p>
                    </div>
                    <div className="radio__container">
                      <label className="radio__circle">
                        <input
                          type="radio"
                          name="loan"
                          checked={loanInputOption === 'amount'}
                          onClick={() => setLoanInputOption('amount')}
                          id="GL_loan-amount"
                        />
                        <span className="radio__circle-checkmark"></span>
                        <p className="text--regular text--black radio__circle-label">
                          Required Loan Amount
                        </p>
                      </label>
                      <label className="radio__circle">
                        <input
                          type="radio"
                          name="loan"
                          checked={loanInputOption === 'weight'}
                          onClick={() => setLoanInputOption('weight')}
                          id="GL_gold-weight"
                        />
                        <span className="radio__circle-checkmark"></span>
                        <p className="text--regular text--black radio__circle-label">
                          My Gold Weight
                        </p>
                      </label>
                    </div>
                    <form>
                      {loanInputOption === 'amount' && (
                        <>
                          <div
                            className={clsx(
                              'input-container',
                              errors.loanAmountInput?.message && 'inValid'
                            )}
                          >
                            <div className="input-form">
                              <span>₹</span>
                              <input
                                type="text"
                                id="loanAmountInput"
                                placeholder="0"
                                {...register('loanAmountInput', {
                                  required: 'Should not be empty',
                                  pattern: {
                                    value: StringPatterns.numberOnly,
                                    message: 'Add valid amount',
                                  },
                                  min: {
                                    value: 1000,
                                    message: 'Minimum value is ₹1000',
                                  },
                                })}
                              />
                            </div>
                            <p className="text--red text--regular errorMessage">
                              {errors.loanAmountInput?.message}
                            </p>
                          </div>
                          <div className="requiredGold">
                            <p className="text--black text--semi title">
                              <Image
                                style={{ color: undefined }}
                                className="img-fluid"
                                src={GoldCoin}
                                alt="gold coin icon"
                              />
                              Min. gold required for the loan*
                            </p>
                            <p className="text--black text--semi">
                              {calculateLTV(
                                watch('loanAmountInput'),
                                liveGoldPricePerGram,
                                'AMOUNT-TO-GOLD'
                              )}{' '}
                              gms
                            </p>
                          </div>
                        </>
                      )}
                      {loanInputOption === 'weight' && (
                        <>
                          <div
                            className={clsx(
                              'input-container',
                              errors.goldWeightInput?.message && 'inValid'
                            )}
                          >
                            <div className="input-form">
                              <input
                                type="text"
                                placeholder="0"
                                id="goldWeightInput"
                                {...register('goldWeightInput', {
                                  required: 'Should not be empty',
                                  pattern: {
                                    value: StringPatterns.floatOnly,
                                    message: 'Add valid weight',
                                  },
                                  validate: (value: string) => {
                                    if (
                                      parseFloat(value) * liveGoldPricePerGram <
                                      1000
                                    ) {
                                      return 'Minimum value is ₹1000';
                                    }
                                    return true;
                                  },
                                })}
                              />
                              <span>gms</span>
                            </div>
                            <p className="text--red text--regular errorMessage">
                              {errors.goldWeightInput?.message}
                            </p>
                          </div>
                          <div className="requiredGold">
                            <p className="text--black text--semi title">
                              <Image
                                style={{ color: undefined }}
                                className="img-fluid"
                                src={RupeeIcon}
                                alt="gold coin icon"
                              />
                              Max eligible loan amount
                            </p>
                            <p className="text--black text--semi">
                              ₹
                              {calculateLTV(
                                parseFloat(watch('goldWeightInput')),
                                liveGoldPricePerGram,
                                'GOLD-TO-AMOUNT'
                              )}{' '}
                            </p>
                          </div>
                        </>
                      )}
                    </form>
                    <div className="action-container">
                      <a
                        href=""
                        className={`custom__button custom__button--dark text--bold position-relative`}
                        onClick={(e) => {
                          e.preventDefault();

                          handleSubmit(onFormSubmit)();
                        }}
                        id="GL_apply-now"
                      >
                        Apply Now
                      </a>
                    </div>
                    <GlNewNote />
                  </div>
                </div>
              </div>
              <div className="col-lg-5 pledge repayment">
                {/* Notifications are not available */}

                {/* <div className="loanDetails__card loanCard tablet-margin">
                  <div className="loanCard__title-withChips">
                    <p className="loanCard__title text--bold text--black">
                      Notifications
                    </p>
                    <a href="#" className="text--darkBlue text--bold moreLink">
                      View All
                    </a>
                  </div>
                  <div className="notification">
                    <a href="#" className="notification__list">
                      <div className="left">
                        <p className="text--black text--bold title">
                          Interest Due
                        </p>
                        <p className="text--gray text--regular desc">
                          Interest of amount ₹1,200 is due in 5 days, repay now
                          to avoid an increase in interest rates
                        </p>
                      </div>
                      <div className="right">
                        <span className="dot"></span>
                      </div>
                    </a>
                    <a href="#" className="notification__list">
                      <div className="left">
                        <p className="text--black text--bold title">
                          Interest Due
                        </p>
                        <p className="text--gray text--regular desc">
                          Interest of amount ₹1,200 is due in 5 days, repay now
                          to avoid an increase in interest rates
                        </p>
                      </div>
                      <div className="right">
                        <span className="dot"></span>
                      </div>
                    </a>
                    <a href="#" className="notification__list">
                      <div className="left">
                        <p className="text--black text--bold title">
                          Interest Due
                        </p>
                        <p className="text--gray text--regular desc">
                          Interest of amount ₹1,200 is due in 5 days, repay now
                          to avoid an increase in interest rates
                        </p>
                      </div>
                      <div className="right">
                        <span className="dot"></span>
                      </div>
                    </a>
                  </div>
                </div> */}

                {featureFlags.release_gl_topup && (
                  <a
                    href=""
                    className="loanDetails__card loanCard actionCard"
                    onClick={(e) => {
                      e.preventDefault();
                      openTopupFlow();
                    }}
                  >
                    <div className="right">
                      <p className="loanCard__title with-desc text--bold text--black">
                        Get A Top-Up Loan Now
                      </p>
                      <p className="loanCard__desc text--regular text--gray mb-0">
                        Check eligibility here
                      </p>
                    </div>

                    <div className="right">
                      <span className="circleArrow-icon"></span>
                    </div>
                  </a>
                )}

                <div className="loanDetails__card loanCard">
                  <p className="loanCard__title text--bold text--black">
                    Bringing the best value for your gold
                  </p>
                  <div className="value__row">
                    <div className="item">
                      <Image
                        style={{ color: undefined }}
                        className="img-fluid"
                        src={seamlessHome}
                        alt="30 minutes door step service"
                      />
                      <p className="text--bold text--darkBlue title">
                        Quick Approval
                      </p>
                      <p className="text--regular text--gray desc">
                        Direct Bank Transfer
                      </p>
                    </div>
                    <div className="item">
                      <Image
                        style={{ color: undefined }}
                        className="img-fluid"
                        src={InterestRateIcon}
                        alt="30 minutes door step service"
                      />
                      <p className="text--bold text--darkBlue title">
                        11.99%* p.a.
                        <p className="text--regular desc text--gray">
                          (0.99%* p.m.)
                        </p>
                      </p>
                      <p className="text--regular text--gray desc">
                        Low Interest Rates
                      </p>
                    </div>
                    <div className="item">
                      <Image
                        style={{ color: undefined }}
                        className="img-fluid"
                        src={atHome}
                        alt="30 minutes door step service"
                      />
                      <p className="text--bold text--darkBlue title">
                        Seamless
                      </p>
                      <p className="text--regular text--gray desc">
                        At Home Service
                      </p>
                    </div>
                  </div>
                  <p className="mfone-text-12 mfone-font-200">*T&C applied.</p>
                  <div className="goldInfo mt-1">
                    <div className="left">
                      <Image
                        style={{ color: undefined }}
                        className="img-fluid"
                        src={GoldRateIcon}
                        alt="gold-rate"
                      />
                    </div>
                    <div className="right">
                      <p className="loanCard__title with-desc text--bold text--black">
                        Your gold is 100% safe with us
                      </p>
                      <p className="loanCard__desc text--regular text--gray mb-0">
                        OTP verification for the loan manager coming for at home
                        visit
                      </p>
                    </div>
                  </div>
                </div>
                <Link
                  href="/help"
                  className="loanDetails__card loanCard actionCard"
                  id="GL_loan-details_help"
                >
                  <div className="left">
                    <Image
                      style={{ color: undefined }}
                      className="img-fluid"
                      src={HelpIcon}
                      alt="help icon"
                    />
                    <p className="loanCard__title text--bold text--black mb-0">
                      Help &amp; Support
                    </p>
                  </div>
                  <div className="right">
                    <span className="circleArrow-icon"></span>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </section>
        <AttractiveSchemes />
        <section className="home__faq with-padding">
          <GlfhFaq />
        </section>
        <a
          className="d-none"
          data-toggle="modal"
          data-target="#notEligibleModal"
          ref={notEligibleModalRef}
        ></a>
        <div
          className="modal addressDetail__modal successModal fade"
          id="notEligibleModal"
          tabIndex={1}
          role="dialog"
          aria-labelledby="notEligibleModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal__title d-flex justify-content-end">
                <button data-dismiss="modal">
                  <Image
                    style={{ color: undefined }}
                    className="img-fluid"
                    src={CloseIcon}
                    alt="close icon"
                  />
                </button>
              </div>
              <div className="successMessage">
                <Image
                  style={{ color: undefined }}
                  className="img-fluid"
                  src={WarningIcon}
                  alt="cancel icon"
                />
                <p className="text--black text--semi title">
                  Sorry! You Are Not Eligible
                </p>
                <p className="text-body-secondary text--semi desc">
                  We currently don&rsquo;t have a 24x7 Top-Up loan offer for you
                </p>
              </div>
            </div>
          </div>
        </div>
        <Loader
          show={isCheckingTopupEligibiliy}
          text="Checking Topup Eligibility"
        />
      </main>
    </>
  );
};
